/**
 * Load a committed picture as a `File`, ready for the share sheet (SPEC-020 D-5).
 *
 * Extracted from `plantSheetFile.js`, which was written for one caller and
 * contained nothing about plants: a same-origin fetch with an abort timeout,
 * wrapped in a `File` with a name and a MIME type. The app's invitation card
 * needs exactly that, so it became the mechanism rather than being copied.
 *
 * `fetch`, the timeout and `File` arrive as defaulted parameters — the repo's
 * rule for anything touching a browser API, and what makes this testable with no
 * network and no share sheet.
 *
 * Same-origin, so the service worker's stale-while-revalidate applies and no
 * CORS is involved. Nothing here is PRECACHED — images never are — so the first
 * share fetches over the network, and offline it throws and the caller falls
 * back to the link.
 */

const REQUEST_TIMEOUT_MS = 10000;

export async function fetchShareImageFile(
    { url, fileName, type = 'image/jpeg' },
    {
        fetchImpl = globalThis.fetch,
        timeoutMs = REQUEST_TIMEOUT_MS,
        FileImpl = globalThis.File,
    } = {},
) {
    if (typeof fetchImpl !== 'function' || typeof FileImpl !== 'function') {
        throw new Error('This environment cannot build a shareable file');
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);

    try {
        const response = await fetchImpl(url, { signal: controller.signal });

        if (!response.ok) {
            throw new Error(`${url} responded ${response.status}`);
        }

        const blob = await response.blob();

        // The MIME type travels with the file, not with the extension: it is what
        // the operating system reads to decide this is a picture rather than an
        // unknown attachment.
        return new FileImpl([blob], fileName, { type });
    } finally {
        clearTimeout(timer);
    }
}
